/**
 * useBatchSearch Hook
 * Provides debounced search functionality for batch IDs by prefix
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { BRRApiClient } from '../services/api';

export type BatchSearchField = 'batch_id' | 'material_id' | 'vendor_batch_id';

interface UseBatchSearchOptions {
  apiClient: BRRApiClient;
  query: string;
  field?: BatchSearchField;
  debounceMs?: number;
  minQueryLength?: number;
}

interface BatchResult {
  batchId: string;
}

interface UseBatchSearchReturn {
  results: BatchResult[];
  loading: boolean;
  error: Error | null;
}

/**
 * Hook that searches batch IDs by prefix with debouncing
 *
 * @example
 * const { results, loading, error } = useBatchSearch({
 *   apiClient: brrApi,
 *   query: searchText,
 *   debounceMs: 400,
 *   minQueryLength: 3,
 *   limit: 20
 * });
 */
export const useBatchSearch = ({
  apiClient,
  query,
  field = 'batch_id',
  debounceMs = 400,
  minQueryLength = 3,
}: UseBatchSearchOptions): UseBatchSearchReturn => {
  const [results, setResults] = useState<BatchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const timeoutRef = useRef<number | null>(null);

  const search = useCallback(async (searchQuery: string) => {
    if (searchQuery.length < minQueryLength) {
      setResults([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.searchBatchIds(searchQuery, undefined, field);
      setResults(response.results);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [apiClient, minQueryLength, field]);

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (query.length < minQueryLength) {
      setResults([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    timeoutRef.current = window.setTimeout(() => {
      void search(query);
    }, debounceMs);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [query, debounceMs, minQueryLength, search]);

  return {
    results,
    loading,
    error
  };
};
